import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import PageContainer from '../../components/layout/PageContainer';
import { FORTA_BLUE, FORTA_ORANGE, FORTA_WHITE } from '../../utils/colors';
import { claimsInvoiceActions, clientActions } from '../../store';
import { a11yProps, TabPanel } from './common';
import { FHSimpleTable, FProgressBar } from '../../components/base';
import {
  getInvoiceListTableColumns,
  invoiceListTableOptions,
  invoiceTableTheme,
} from '../../components/layout/billings/invoices/config';
import { sortInvoicesByStatus } from '../../components/layout/billings/invoices/utils';
import { invoiceHistoryDataTransformer } from '../../components/layout/billing-history/utils';
import { usDollarFormatter } from '../../helpers';
import { Themes } from '../../constants';

import type { RootState } from '../../store';

const InvoiceList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('invoice');
  const { getAccessTokenSilently } = useAuth0();

  const { list: clientList, loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const {
    list: invoicesList,
    clientNotPaidInvoiceCount: notPaidInvoiceCount,
    loading: invoiceLoading,
  } = useSelector((state: RootState) => state.claimsInvoice);

  const [tab, setTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const sortedInvoices = useMemo(() => sortInvoicesByStatus(invoicesList.invoices), [invoicesList]);

  useEffect(() => {
    if (clientList.length === 0) {
      dispatch(clientActions.getList({ getAccessTokenSilently }));
    }
  }, [dispatch, getAccessTokenSilently, clientList.length]);

  useEffect(() => {
    if (clientList.length > 0) {
      dispatch(
        claimsInvoiceActions.getList({
          getAccessTokenSilently,
          params: {
            client_id: clientList?.map((client) => client.id),
            not_paid_invoices: true,
          },
        }),
      );
    }
  }, [dispatch, getAccessTokenSilently, clientList]);

  useEffect(() => {
    if (clientList.length > 0) {
      const params = {
        client_id: clientList[tab]?.id,
      };
      dispatch(claimsInvoiceActions.getList({ getAccessTokenSilently, params }));
    }
  }, [dispatch, getAccessTokenSilently, clientList, tab]);

  if (clientLoading) {
    return <FProgressBar />;
  }

  return (
    <PageContainer>
      {invoiceLoading && <FProgressBar />}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: 2,
          justifyContent: 'space-between',
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="Client tabs"
          sx={{
            mt: 3,
            minHeight: 32,
            '& .MuiTab-root': {
              padding: '0 0.5rem',
              textTransform: 'none',
              fontWeight: 400,
              minHeight: 32,
              color: FORTA_BLUE.primary,
            },
            '& .MuiTabs-flexContainer': {
              gap: '1.5rem',
            },
          }}
        >
          {clientList?.map((client, index) => (
            <Tab
              key={client.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Typography sx={{ fontSize: '24px', fontWeight: '400' }}>
                    {client.first_name} {client.last_name}
                  </Typography>
                  {client.id && notPaidInvoiceCount[client.id] > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '20px',
                        height: '20px',
                        backgroundColor: FORTA_ORANGE,
                        borderRadius: '50%',
                      }}
                    >
                      <Typography sx={{ color: FORTA_WHITE, fontWeight: 800, fontSize: '13px' }}>
                        {notPaidInvoiceCount[client.id]}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
              {...a11yProps(index)}
              data-testid={`clientTab-${index}`}
            />
          ))}
        </Tabs>
        {clientList.length > 0 && clientList[tab].id && invoicesList.total_amount_due > 0 && (
          <Typography
            sx={{
              mt: 2,
              color: FORTA_BLUE.primary,
              fontSize: '24px',
              fontWeight: '400',
            }}
          >
            Total due: {usDollarFormatter.format(invoicesList.total_amount_due)}
          </Typography>
        )}
      </Box>
      {clientList?.map(
        (client, index) =>
          !invoiceLoading && (
            <TabPanel key={client.id} value={tab} index={index}>
              {sortedInvoices.length > 0 ? (
                <FHSimpleTable
                  title=" "
                  data={invoiceHistoryDataTransformer(sortedInvoices)}
                  columnDefinitions={getInvoiceListTableColumns(t)}
                  columnOptions={invoiceListTableOptions}
                  tableTheme={invoiceTableTheme(Themes.Primary)}
                />
              ) : (
                <Typography
                  sx={{
                    mt: 2,
                  }}
                >
                  {t('noInvoices')}
                </Typography>
              )}
            </TabPanel>
          ),
      )}
    </PageContainer>
  );
};

export default InvoiceList;
